import React, { useState, useRef, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
// Extend Window interface for Speech Recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
import {
  Mic,
  MicOff,
  Volume2,
  ArrowLeftRight,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { speak } from "@/lib/ttsService";
import { useTranslation } from "@/hooks/useTranslation";

// Supported languages
const LANGUAGES = [
  { code: "he-IL", name: "עברית", flag: "🇮🇱", shortCode: "he" },
  { code: "en-US", name: "English", flag: "🇺🇸", shortCode: "en" },
  { code: "ar-SA", name: "العربية", flag: "🇸🇦", shortCode: "ar" },
  { code: "ru-RU", name: "Русский", flag: "🇷🇺", shortCode: "ru" },
  { code: "fr-FR", name: "Français", flag: "🇫🇷", shortCode: "fr" },
  { code: "es-ES", name: "Español", flag: "🇪🇸", shortCode: "es" },
];

interface ConversationEntry {
  id: string;
  original: string;
  translated: string;
  sourceLang: string;
  targetLang: string;
  timestamp: Date;
}

export default function Translator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [sourceLang, setSourceLang] = useState("he-IL");
  const [targetLang, setTargetLang] = useState("en-US");
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [conversationHistory, setConversationHistory] = useState<ConversationEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Handle language selection from URL params (returning from language picker page)
  useEffect(() => {
    const newSourceLang = searchParams.get("sourceLang");
    const newTargetLang = searchParams.get("targetLang");
    if (newSourceLang) setSourceLang(newSourceLang);
    if (newTargetLang) setTargetLang(newTargetLang);
  }, [searchParams]);

  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isSupported = "webkitSpeechRecognition" in window || "SpeechRecognition" in window;

  const sourceLangInfo = LANGUAGES.find((l) => l.code === sourceLang) || LANGUAGES[0];
  const targetLangInfo = LANGUAGES.find((l) => l.code === targetLang) || LANGUAGES[1];

  // Keep ref in sync with state
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversationHistory, interimTranscript]);

  // Initialize speech recognition once
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, [isSupported]);

  // Setup event handlers (separate effect to handle translation function changes)
  useEffect(() => {
    if (!recognitionRef.current) return;

    const recognition = recognitionRef.current;

    recognition.onresult = (event: any) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      setInterimTranscript(interim);

      if (final) {
        handleTranslation(final);
      }
    };

    recognition.onerror = (event: any) => {
      // Ignore "aborted" errors when intentionally stopping
      if (event.error === "aborted") return;
      
      console.error("Speech recognition error:", event.error);
      if (event.error === "not-allowed") {
        setError(t("allowMicAccess"));
      } else if (event.error === "no-speech") {
        // Don't show error for no speech, just restart if still listening
        if (isListeningRef.current) {
          try {
            recognition.start();
          } catch (e) {
            // Already started
          }
        }
        return;
      } else {
        setError(`${t("speechRecognitionError")}: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      // Restart if still supposed to be listening
      if (isListeningRef.current) {
        try {
          recognition.start();
        } catch (e) {
          // Already started or other error
        }
      }
    };
  }, [sourceLang, targetLang]);

  // Translate using MyMemory API (free, no key required)
  const handleTranslation = async (text: string) => {
    setInterimTranscript("");

    // Get language codes for translation
    const sourceLangCode = sourceLangInfo.shortCode;
    const targetLangCode = targetLangInfo.shortCode;

    let translated = text;

    try {
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${sourceLangCode}|${targetLangCode}`
      );
      const data = await response.json();

      if (data.responseStatus === 200 && data.responseData?.translatedText) {
        translated = data.responseData.translatedText;
      }
    } catch (err) {
      console.error("Translation error:", err);
      translated = `[${t("translationError")}: ${text}]`;
    }

    const entry: ConversationEntry = {
      id: Date.now().toString(),
      original: text,
      translated: translated,
      sourceLang,
      targetLang,
      timestamp: new Date(),
    };

    setConversationHistory((prev) => [...prev, entry]);

    // Auto-speak the translation
    speakText(translated, targetLang);
  };

  const toggleListening = () => {
    if (!isSupported || !recognitionRef.current) return;

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Already stopped
      }
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.lang = sourceLang;
        recognitionRef.current.start();
        setIsListening(true);
        setError(null);
      } catch (e: any) {
        if (e.message?.includes("already started")) {
          // Already running, just update state
          setIsListening(true);
        } else {
        console.error("Failed to start recognition:", e);
        setError(t("speechRecognitionError"));
      }
    }
    }
  };

  const handleSwapLanguages = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
  };

  const clearHistory = () => {
    setConversationHistory([]);
  };

  const speakText = (text: string, lang: string) => {
    // Extract short language code from full code (e.g., "he-IL" -> "he")
    const shortLang = lang.split("-")[0];
    speak({ text, language: shortLang });
  };

  return (
    <>
      <Helmet>
        <title>מתרגם בזמן אמת - EyeControl App</title>
        <meta name="description" content="מתרגם קולי בזמן אמת עם זיהוי דיבור" />
      </Helmet>

      <MainLayout onBack={() => navigate("/")}>
        <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-card/50">
            <h1 className="text-xl font-bold text-foreground">{t("realtimeTranslator")}</h1>
            <div className="w-20" />
          </div>

          {/* Language Control Bar */}
          <div className="flex items-center justify-center gap-3 p-4 bg-card/50 border-b border-border">
            {/* Source Language */}
            <button
              onClick={() => navigate(`/translator/language?type=source&current=${sourceLang}&exclude=${targetLang}`)}
              data-scan-item
              className={cn(
                "flex flex-col items-center gap-1 px-5 py-3 rounded-2xl border-2 transition-all min-w-[120px]",
                "bg-background border-primary",
                "hover:bg-primary/10 active:scale-95"
              )}
            >
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-primary" />
                <span className="text-xs text-primary font-medium">{t("speaking")}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{sourceLangInfo.flag}</span>
                <span className="text-base font-bold text-foreground">
                  {sourceLangInfo.name}
                </span>
              </div>
            </button>

            {/* Swap Button */}
            <button
              onClick={handleSwapLanguages}
              data-scan-item
              className="p-3 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-all active:scale-95"
            >
              <ArrowLeftRight className="w-5 h-5" />
            </button>

            {/* Target Language */}
            <button
              onClick={() => navigate(`/translator/language?type=target&current=${targetLang}&exclude=${sourceLang}`)}
              data-scan-item
              className={cn(
                "flex flex-col items-center gap-1 px-5 py-3 rounded-2xl border-2 transition-all min-w-[120px]",
                "bg-background border-border",
                "hover:border-primary active:scale-95"
              )}
            >
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-medium">{t("translation")}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{targetLangInfo.flag}</span>
                <span className="text-base font-bold text-foreground">
                  {targetLangInfo.name}
                </span>
              </div>
            </button>
          </div>

          {/* Error Message */}
          {!isSupported && (
            <div className="mx-4 mt-4 p-4 rounded-xl bg-destructive/10 border border-destructive/30 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <span className="text-destructive text-sm">
                {t("browserNotSupported")}
              </span>
            </div>
          )}

          {error && (
            <div className="mx-4 mt-4 p-4 rounded-xl bg-destructive/10 border border-destructive/30 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <span className="text-destructive text-sm">{error}</span>
            </div>
          )}

          {/* Conversation Stream */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {conversationHistory.length === 0 && !interimTranscript && (
              <div className="flex-1 flex items-center justify-center h-full min-h-[200px]">
                <p className="text-muted-foreground text-center">
                  {t("pressMicToSpeak")}
                </p>
              </div>
            )}

            <AnimatePresence>
              {conversationHistory.map((entry) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-card rounded-2xl border-2 border-border p-4 space-y-2"
                >
                  <p className="text-sm text-muted-foreground">{entry.original}</p>
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xl font-bold text-foreground flex-1">
                      {entry.translated}
                    </p>
                    <button
                      onClick={() => speakText(entry.translated, entry.targetLang)}
                      data-scan-item
                      className="p-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all active:scale-95"
                    >
                      <Volume2 className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Interim Transcript */}
            {interimTranscript && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-card/50 rounded-2xl border-2 border-dashed border-border p-4"
              >
                <p className="text-muted-foreground animate-pulse">{interimTranscript}</p>
              </motion.div>
            )}
          </div>

          {/* Action Area */}
          <div className="pb-6 pt-4 px-6 border-t border-border bg-card/50">
            <div className="flex items-center justify-center gap-4">
              {/* Clear Button */}
              {conversationHistory.length > 0 && (
                <button
                  onClick={clearHistory}
                  data-scan-item
                  className="p-4 rounded-2xl bg-background text-muted-foreground border-2 border-border hover:text-destructive hover:border-destructive transition-all active:scale-95"
                >
                  <Trash2 className="w-6 h-6" />
                </button>
              )}

              {/* Mic Button */}
              <button
                onClick={toggleListening}
                disabled={!isSupported}
                data-scan-item
                className={cn(
                  "relative w-24 h-24 rounded-full transition-all flex items-center justify-center",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  isListening
                    ? "bg-destructive text-destructive-foreground shadow-lg"
                    : "bg-primary text-primary-foreground shadow-lg hover:opacity-90",
                  "active:scale-95"
                )}
              >
                {isListening && (
                  <span className="absolute inset-2 rounded-full bg-destructive/60 animate-ping" />
                )}
                {isListening ? (
                  <MicOff className="w-10 h-10 relative z-10" />
                ) : (
                  <Mic className="w-10 h-10 relative z-10" />
                )}
              </button>
            </div>

            <p
              className={cn(
                "text-center mt-4 text-sm font-medium",
                isListening ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {isListening ? t("listening") : t("pressToStart")}
            </p>

            {/* Sound Wave Animation */}
            <div className="h-8 flex items-center justify-center mt-3">
              {isListening && (
                <div className="flex items-center justify-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-destructive rounded-full"
                      animate={{ height: [8, 24, 8] }}
                      transition={{
                        duration: 0.5,
                        repeat: Infinity,
                        delay: i * 0.1,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
}
